<template>
	<div
		@keydown.enter="() => $emit('submit')"
		class="tw-mx-auto tw-py-8 tw-w-full"
		ref="mainOtpElement">
		<label
			for="otp"
			v-html="t('platform.label_one_time_password')"
			class="tw-font-semibold md:tw-text-xl"></label>
		<div
			v-if="validationMessages.length > 0"
			class="tw-flex tw-flex-col tw-items-start tw-justify-center">
			<span
				class="tw-text-xs tw-text-status-red"
				v-for="message in validationMessages"
				:key="message"
				v-html="t(message)"></span>
		</div>
		<div class="tw-flex tw-flex-row tw-items-center tw-justify-between">
			<div
				class="tw-flex tw-justify-between tw-space-x-2 tw-w-72"
				@paste.prevent="handlePaste">
				<input
					v-for="(item, index) in inputFields"
					:key="index"
					v-model="inputFields[index]"
					class="tw-input-base tw-text-center tw-w-auto tw-h-10 tw-text-lg tw-px-0 !tw-border-gray-400 focus:!tw-border-secondary-500"
					type="tel"
					:placeholder="item"
					autocomplete="one-time-code"
					inputmode="numeric"
					pattern="[0-9]*"
					maxlength="1"
					@input="moveFocus(index)"
					:ref="(setInputRef as VNodeRef)"
					:aria-label="t('platform.label_one_time_password')"
					@click="() => handleClick(index)"
					@keydown.backspace.prevent="() => handleBackspace(index)"
					@keydown.right.prevent="() => moveFocus(index)"
					@keydown.left.prevent="() => moveFocusBack(index)" />
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import {nextTick, ref, Ref, VNodeRef, watch} from "vue";

const { t } = useTranslation();
const inputFields = ref<string[]>(new Array(6).fill(""));
const inputRefs: Ref<HTMLInputElement[]> = ref([]);
const mainOtpElement: Ref<HTMLElement | null> = ref(null);
const validationMessages = ref<string[]>([]);
const activeValidationTimeout = ref<NodeJS.Timeout | null>(null);
const setInputRef = (el: Element | null) => {
	if (el && el instanceof HTMLInputElement) {
		inputRefs.value.push(el);
	}
};

const moveFocus = (index: number) => {
	nextTick(() => {
		if (index < inputFields.value.length - 1 && inputFields.value[index].length === 1) {
			inputRefs.value[index + 1].focus();
			inputRefs.value[index + 1].select();
			timeBasedCharNumberValidation();
		}
	});
};

const moveFocusBack = (index: number) => {
	nextTick(() => {
		if (index > 0) inputRefs.value[index - 1].focus();
		if (index > 0) inputRefs.value[index - 1].select();
		timeBasedCharNumberValidation();
	});
};

const handlePaste = (event: ClipboardEvent) => {
	const pasteData = event.clipboardData?.getData("text")?.slice(0, 6);
	if (pasteData && /^\d{6}$/.test(pasteData)) {
		pasteData.split("").forEach((char, index) => {
			inputFields.value[index] = char;
		});
		nextTick(() => {
			inputRefs.value[inputFields.value.length - 1].focus();
		});
		validateAndEmit(pasteData);
	}
};

const handleBackspace = (index: number) => {
	inputFields.value[index] = "";

	nextTick(() => {
		if (index > 0) inputRefs.value[index - 1].focus();
		if (index > 0) inputRefs.value[index - 1].select();
		validateAndEmit(inputFields.value.join(""));
	});
};

const handleClick = (index: number) => {
	nextTick(() => {
		inputRefs.value[index].focus();
		inputRefs.value[index].select();
	});
};

const timeBasedCharNumberValidation = () => {
	if (activeValidationTimeout.value) {
		clearTimeout(activeValidationTimeout.value);
	}
	activeValidationTimeout.value = setTimeout(() => {
		const numberOfDigitsRegex = /\w{6}/;
		if (!numberOfDigitsRegex.test(inputFields.value.join(""))) {
			if (!validationMessages.value.includes("validation.otp_length")) {
				validationMessages.value.push("validation.otp_length");
				emits("update", "");
			}
		}
	}, 2000);
};

const validateAndEmit = (val: string) => {
	const numOnlyRegex = /^[0-9]*$/;

	timeBasedCharNumberValidation();

	if (!numOnlyRegex.test(val)) {
		if (!validationMessages.value.includes("validation.numbers_only")) {
			validationMessages.value.push("validation.numbers_only");
		}
		emits("update", "");
		return;
	}
	if (val.length < 6) {
		emits("update", "");
		return;
	}
	validationMessages.value = [];
	emits("update", val);
};
const emits = defineEmits<{
	/**
	 * Emits the OTP value only when it is valid
	 */
	(e: "update", v: string): void;
	/**
	 * It triggers when `Enter` key is pressed regardless of the validation.
	 */
	(e: "submit"): void;
}>();

const props = defineProps({
	/**
	 * Optional prop. Used for resetting the OTPInput fields.
	 */
	resetCounter: {
		type: Number,
		default: 0,
	},
});

const handleReset = () => {
	inputFields.value = new Array(6).fill("");
	inputRefs.value[0].focus();
};
watch(
	inputFields,
	(newValue) => {
		validateAndEmit(newValue.join(""));
	},
	{
		deep: true,
	}
);

watch(
	() => props.resetCounter,
	() => {
		handleReset();
	}
);
</script>